import { Observable } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import { isAuthLinkAuthenticationError } from "@/lib/errors/authErrors";
import { clearAllTokens, setAccessToken, setIdToken, setRefreshToken } from "@/lib/tokenManager";

let isRefreshing = false;
let pendingRequests: Array<(value: boolean) => void> = [];

const handleAuthenticationError = (): void => {
  if (typeof window === "undefined") return;

  clearAllTokens();

  if (window.location.pathname !== "/signin") {
    window.location.replace("/signin");
  }
};

const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
      }
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
      }
      if (data.idToken) {
        setIdToken(data.idToken);
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return false;
  }
};

export const errorLink = onError(({ error, operation, forward }) => {
  if (typeof window === "undefined") {
    return;
  }

  let isAuthError = false;

  if (CombinedGraphQLErrors.is(error)) {
    // GraphQL errors
    for (const graphQLError of error.errors) {
      console.error(
        `[GraphQL error]: Message: ${graphQLError.message}, Path: ${graphQLError.path?.join(" > ")}, Operation: ${operation.operationName}`
      );

      if (isAuthLinkAuthenticationError(graphQLError)) {
        isAuthError = true;
      }
    }
  } else if (error && "statusCode" in error && error.statusCode === 401) {
    // Network 401 errors
    console.error(`[Network error]: ${error.message}, Operation: ${operation.operationName}`);
    isAuthError = true;
  } else if (error) {
    // Other network errors
    console.error(`[Network error]: ${error.message}, Operation: ${operation.operationName}`);
  }

  if (isAuthError) {
    const alreadyRetried = operation.getContext().alreadyRetried;

    if (alreadyRetried) {
      handleAuthenticationError();
      return;
    }

    if (!isRefreshing) {
      isRefreshing = true;

      return new Observable((observer) => {
        refreshAccessToken()
          .then((success) => {
            if (success) {
              for (const resolve of pendingRequests) {
                resolve(true);
              }
              pendingRequests = [];
              operation.setContext({ alreadyRetried: true });
              forward(operation).subscribe(observer);
            } else {
              for (const resolve of pendingRequests) {
                resolve(false);
              }
              pendingRequests = [];
              handleAuthenticationError();
              observer.complete();
            }
          })
          .catch(() => {
            for (const resolve of pendingRequests) {
              resolve(false);
            }
            pendingRequests = [];
            handleAuthenticationError();
            observer.complete();
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    } else {
      return new Observable((observer) => {
        pendingRequests.push((success) => {
          if (success) {
            operation.setContext({ alreadyRetried: true });
            forward(operation).subscribe(observer);
          } else {
            observer.complete();
          }
        });
      });
    }
  }
});
